import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputBase,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography
} from '@mui/material';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useNodesState} from 'reactflow';
import {useParams} from 'react-router-dom';
import {useJourney, useJourneyStep} from '../../../../api/Journeys';
import useLocalStore from '../../../../useLocalStore';

import {
    DeleteRounded
} from '@mui/icons-material';
import {useConfirm} from 'material-ui-confirm';
import {enqueueSnackbar} from 'notistack';
import * as http from '../../../../util/elemental_axios_client';
import BranchFilterSidebar from './BranchFilterSidebar';
import WaitSidebar from './WaitSidebar';
import MessageSMSSidebar from './MessageSMSSidebar';
import MessageEmailSidebar from './MessageEmailSidebar';
import SpigotSidebar from './SpigotSidebar';
import MergeSidebar from './Merge/MergeSidebar';
import { StepTypeIcon } from '../../JourneyConstants';

const DeleteStepDialog = ({ open, onClose, onConfirm }) => {
    const [branchNextEdgeKeyToFollowOnDelete, setBranchNextEdgeKeyToFollowOnDelete] = useState('match');

    const handleBranchNextEdgeKeySelection = (event) => {
        setBranchNextEdgeKeyToFollowOnDelete(event.target.value);
    };

    const handleConfirm = () => {
        onConfirm(branchNextEdgeKeyToFollowOnDelete);
    };

    return (
        <Dialog open={open} onClose={onClose}
                sx={{
                    zIndex: 1450,
                    '& .MuiDialog-paper': {
                        zIndex: (theme) => 1450 + 2,
                    },
                }}
        >
            <DialogTitle>Delete step</DialogTitle>
            <DialogContent>
                <p>Select the branch that you want to keep.</p>
                <FormControl fullWidth size='small' sx={{ mt: 2 }}>
                    <InputLabel id='branch-label'>Branch</InputLabel>
                    <Select
                        labelId='branch-label'
                        value={branchNextEdgeKeyToFollowOnDelete}
                        onChange={handleBranchNextEdgeKeySelection}
                        label='Branch'

                        MenuProps={{
                            style: {zIndex: 35001}
                        }}

                    >
                        <MenuItem value='match'>Match</MenuItem>
                        <MenuItem value='no_match'>No Match</MenuItem>
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} color='primary'>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};




const JourneyStepEditSidebar = ({ history }) => {
    const queryClient = useQueryClient();
    const { id: journeyIdStr } = useParams();
    const journeyId = parseInt(journeyIdStr);

    const [getSidebar, resetSidebar] = useLocalStore((state) => [state.getSidebar, state.resetSidebar]);
    const sidebar = getSidebar();

    // left for now since it is used to pass in the prev edge key for branches which powers the deletion branch chooser and is used in the branch component at the moment
    const stepState = sidebar.params;
    const stepId = stepState.stepId;

    const confirm = useConfirm();

    const [ showEditLabel, setShowEditLabel] = useState(false);
    const [ label, setLabel ] = useState(false);

    const { data: journeyStep, isSuccess: isSuccessJourneyStep } = useJourneyStep(journeyId, stepId);
    const { data: journey, isSuccess: isSuccessJourney } = useJourney(journeyId);

    const [showDeleteChooseBranchDialog, setShowDeleteChooseBranchDialog] = useState(false);
    const [deleteState, setDeleteState] = useState({ can_delete: true, popover_message: null });

    const handleCanDeleteChange = (newDeleteState) => {
        setDeleteState(newDeleteState);
    };


    const mutationUpdateStepLabel = useMutation( {
        // @ts-ignore
        mutationFn: ({ payload }) => {
            return http.put(`/flows/${journey.id}/steps/common/${journeyStep.id}/label`, payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['journey_step', journeyStep.id]);
        }
    });

    const mutationDeleteStep = useMutation({
        // @ts-ignore
        mutationFn: ( { stepId, payload } ) => {
            return http.remove(`/flows/${journey.id}/steps/${stepId}`, payload);
        },
        onSuccess: () => {
            // TODO: ideally it actually just sets the new adjacency list from the return to avoid a double fetch
            queryClient.invalidateQueries(['journeys', journey.id]);
            // queryClient.invalidateQueries(['journeys', journey.id, 'adjacencyList', 'draft']);
            resetSidebar();
            enqueueSnackbar(`${journeyStep?.type} step deleted successfully`);
        }
    });

    // When updating the labels, this reference allows updating it in the state
    const [nodes, setNodes, onNodesChange] = useNodesState([]);

    // this should be part of the step type config structure
    const hasCustomWidth = (journey?.status === 'active' && (journeyStep?.type === 'email' || journeyStep?.type === 'sms')) || (journeyStep?.type === 'email' || journeyStep?.type === 'sms');

    useEffect(() => {
        journeyStep && setLabel(journeyStep.label);
    }, [journeyStep]);


    const onChangeLabel = () => {
        setShowEditLabel((state) => !state);
    };

    const handleStepLabelChange = (e) => {
        setLabel(e.target.value);
    };

    const onStepLabelUpdate = () => {
        if(journeyStep?.label !== label) {
            setNodes(nodes.filter(el => el.data.stepId !== stepId));
            // @ts-ignore
            mutationUpdateStepLabel.mutate( {payload: {label}} );
        }
        setShowEditLabel(false);
    };

    const handleDeleteStepButtonClick = () => {
        // Check the condition here
        const shouldShowDialog = journeyStep?.type === 'branch_filter';

        if (shouldShowDialog) {

            setShowDeleteChooseBranchDialog(true);
        } else {
            // Call the other method directly
            confirm( {
                description: `Are you sure you want to delete this ${journeyStep?.type} step: "${journeyStep?.description_minimal || journeyStep?.description || 'No description'}"?`,
                dialogProps: {
                    sx: {
                        zIndex: 1450,
                        '& .MuiDialog-paper': {
                            zIndex: (theme) => 1450 + 1,
                        }
                    },
                }
            }).then(
                () => {
                    //
                    // @ts-ignore
                    mutationDeleteStep.mutate({ stepId, payload: {}});
                }).catch(
                () => {}
            );
        }
    };

    const handleDeleteBranchDialogConfirm = (branchNextEdgeKeyToFollowOnDelete) => {

        setShowDeleteChooseBranchDialog(false);
        confirm({
            title: 'Delete step',
            description: `Are you sure you want to delete this ${journeyStep?.type} step: "${journeyStep?.description_minimal || journeyStep?.description || 'No description'}" and keep the "${branchNextEdgeKeyToFollowOnDelete}" branch?`,
            confirmationText: 'Delete',
            cancellationText: 'Cancel',
            dialogProps: {
                sx: {
                    zIndex: 1450,
                    '& .MuiDialog-paper': {
                        zIndex: (theme) => 1450 + 1,
                    }
                },
            }
        }).then(() => {


            // Implement your delete logic here
            // @ts-ignore
            mutationDeleteStep.mutate({stepId, payload: { follow_key_from: branchNextEdgeKeyToFollowOnDelete }});
        }).catch(() => {
            // User canceled the action .. nothing to worry about
        });
    };

    if ( isSuccessJourneyStep && isSuccessJourney ) {

        const getSidebarIcon = (step, color) => {
            return <StepTypeIcon step={step} color={color} />;
        };

        const sidebars = {
            'branch_filter': [getSidebarIcon(journeyStep, 'success'), <BranchFilterSidebar stepId={journeyStep.id}  hideSidebar={resetSidebar} />],
            'wait': [getSidebarIcon(journeyStep, 'warning'), <WaitSidebar stepId={journeyStep.id}  hideSidebar={resetSidebar} />],
            'email': [getSidebarIcon(journeyStep, 'secondary'), <MessageEmailSidebar stepId={journeyStep.id} hideSidebar={resetSidebar} />],
            'sms': [getSidebarIcon(journeyStep, 'secondary'), <MessageSMSSidebar stepId={journeyStep.id} hideSidebar={resetSidebar} history={history} />],
            'spigot': [getSidebarIcon(journeyStep, 'info'), <SpigotSidebar stepId={journeyStep.id}  hideSidebar={resetSidebar} />],
            'merge': [getSidebarIcon(journeyStep, 'secondary'), <MergeSidebar stepId={journeyStep.id} hideSidebar={resetSidebar} onCanDeleteChange={handleCanDeleteChange} />]
        };

        return (
            <Box className='flow-sidebar' display='flex' style={{width: hasCustomWidth ? '22vw' : '15vw'}}>
                <Box className='flow-sidebar__header'>
                    <Box display="flex" alignItems="center">
                        <StepTypeIcon step={journeyStep} style={{ marginRight: 8 }} color={undefined} />
                        <div onBlur={onStepLabelUpdate}>
                            {showEditLabel ? (
                                <InputBase id='journey-name' value={label} onChange={handleStepLabelChange}
                                           className='flow-sidebar__title' autoFocus/>
                            ) : (
                                <Box onClick={onChangeLabel}>
                                    <Tooltip placement='right' title={'Click to change'}>
                                        <Typography component='div' variant='subtitle1' className='flow-sidebar_title'>
                                            {label}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            )}
                        </div>
                    </Box>
                    <Box display='inline-flex' alignItems='center'>

                        <Tooltip 
                            placement='top' 
                            title={deleteState.popover_message || 'Delete Step'}
                        >
                            <span>
                                <Button
                                    variant='contained'
                                    startIcon={<DeleteRounded />}
                                    size='small'
                                    onClick={() => handleDeleteStepButtonClick()}
                                    disabled={!deleteState.can_delete}
                                >
                                    Delete Step
                                </Button>
                            </span>
                        </Tooltip>
                        <DeleteStepDialog
                            open={showDeleteChooseBranchDialog}
                            onClose={() => setShowDeleteChooseBranchDialog(false)}
                            onConfirm={handleDeleteBranchDialogConfirm}
                        />




                    </Box>
                </Box>
                {/*{(neutralState.error?.errors || neutralState.error) &&*/}
                {/*    <Box mt={4} mr={2} ml={2}>*/}
                {/*        <Alert severity='warning'>Please check your conditions again</Alert>*/}
                {/*    </Box>}*/}
                {sidebars[journeyStep.type][1]}
            </Box>
        );
    }
    else {
        return <Box>Loading ..</Box>;
    }
};

export default JourneyStepEditSidebar;
